import { Link, useParams, useLocation } from "wouter";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import { BandPill, BandBar, bandStyle } from "@/components/Band";
import {
  QBANK,
  scoreDimension,
  parentQuestions,
  subQuestionsOf,
  findOption,
  type Question,
  type AnswerOption,
  type AnswerMap,
} from "@/lib/scoring";
import { api } from "@/lib/session";
import { ArrowLeft, ArrowDown, Check } from "lucide-react";

interface ApiQuestion {
  key: string;
  answer: { selectedOption: string } | null;
}

interface ApiAccount {
  id: number;
  name: string;
  industry: string;
  stage: string;
}

function questionsToAnswerMap(questions: ApiQuestion[]): AnswerMap {
  const out: AnswerMap = {};
  for (const q of questions) {
    if (q.answer?.selectedOption) {
      out[q.key] = q.answer.selectedOption;
    }
  }
  return out;
}

export default function DimensionFlow() {
  const { id, dim } = useParams<{ id: string; dim: string }>();
  const [, navigate] = useLocation();

  const [account, setAccount] = useState<ApiAccount | null>(null);
  const [answers, setAnswers] = useState<AnswerMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-question AbortController map — allows cancelling stale in-flight saves
  const abortByKey = useRef<Map<string, AbortController>>(new Map());

  const dimension = QBANK.dimensions.find((d) => d.dimension_id === dim);

  // Load account and answers on mount
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      api<ApiAccount>(`/api/accounts/${id}`),
      api<ApiQuestion[]>(`/api/accounts/${id}/questions`),
    ])
      .then(([acc, questions]) => {
        if (cancelled) return;
        setAccount(acc);
        setAnswers(questionsToAnswerMap(questions));
      })
      .catch((e: any) => {
        if (!cancelled) setError(e?.message ?? "Failed to load.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [id]);

  const result = useMemo(
    () => (dimension ? scoreDimension(dimension, answers) : null),
    [dimension, answers]
  );

  const setAnswer = useCallback(
    (qid: string, letter: string) => {
      // Optimistically update local state
      setAnswers((prev) => {
        const next = { ...prev, [qid]: letter };
        // If parent answer changes and de-activates a sub-block, clear sub answers
        const q = dimension?.questions.find((x) => x.question_id === qid);
        if (q && q.parent_question_id === null) {
          const subs = dimension ? subQuestionsOf(dimension, qid) : [];
          if (subs.length > 0 && !subs[0].trigger_options.includes(letter)) {
            for (const sq of subs) {
              delete next[sq.question_id];
            }
          }
        }
        return next;
      });

      // Persist to API — abort any previous in-flight save for the same question,
      // then fire-and-forget. Local state is already updated optimistically above.
      if (!id) return;
      const prev = abortByKey.current.get(qid);
      prev?.abort();
      const ctrl = new AbortController();
      abortByKey.current.set(qid, ctrl);
      api(`/api/accounts/${id}/answers/${encodeURIComponent(qid)}`, {
        method: "PUT",
        body: JSON.stringify({ selectedOption: letter }),
        signal: ctrl.signal,
      }).catch((e) => {
        if (e?.name === "AbortError") return;
        console.error("save failed", qid, e);
      });
    },
    [id, dimension]
  );

  // D-034 (2026-05-20): replace legacy white PageShell with dark OperatorShell
  // so dimension drill-in pages match the rest of the post-login experience.
  if (loading) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Home", href: "/" }, { label: "Accounts", href: "/accounts" }, { label: "Dimension" }]}>
        <div className="card-shell p-8 text-sm text-muted-foreground">Loading...</div>
      </OperatorShell>
    );
  }

  if (error || !account || !dimension || !result) {
    return (
      <OperatorShell breadcrumbs={[{ label: "Home", href: "/" }, { label: "Accounts", href: "/accounts" }, { label: "Dimension" }]}>
        <div className="card-shell p-8 text-sm text-muted-foreground">
          {error ?? "Dimension not found."}
          <Link href={`/account/${id}`} className="underline ml-2">
            Back
          </Link>
        </div>
      </OperatorShell>
    );
  }

  const parents = parentQuestions(dimension);
  const s = bandStyle(result.color);
  const pctTbd = Math.round(result.tbdWeightShare * 100);

  // Progress: count parent questions with a non-TBD answer
  const answeredParents = parents.filter((p) => {
    const a = answers[p.question_id];
    if (!a) return false;
    const opt = findOption(p, a);
    return opt ? !opt.is_tbd : false;
  }).length;
  const totalParents = parents.length;
  const pctAnswered = totalParents > 0 ? Math.round((answeredParents / totalParents) * 100) : 0;
  const nextParentIndex = parents.findIndex((p) => {
    const a = answers[p.question_id];
    if (!a) return true;
    const opt = findOption(p, a);
    return !opt || opt.is_tbd;
  });
  const currentQuestionNumber = nextParentIndex === -1 ? totalParents : nextParentIndex + 1;

  const jumpToNextUnanswered = () => {
    if (nextParentIndex === -1) return;
    const target = parents[nextParentIndex];
    const el = document.getElementById(`q-${target.question_id}`);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 140;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <OperatorShell
      breadcrumbs={[
        { label: "Home", href: "/" },
        { label: "Accounts", href: "/accounts" },
        { label: account.name, href: `/account/${account.id}` },
        { label: dimension.short_name },
      ]}
    >
      <div className="mb-6">
        <Link
          href={`/account/${account.id}`}
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> {account.name} scorecard
        </Link>
      </div>

      {/* Sticky progress bar */}
      <div className="sticky top-0 z-30 -mx-6 px-6 py-3 mb-6 bg-[hsl(var(--background))]/90 backdrop-blur supports-[backdrop-filter]:bg-[hsl(var(--background))]/75 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground tabular shrink-0">
              {dimension.short_name}
            </span>
            <span className="text-[11px] tabular text-foreground shrink-0">
              Q {currentQuestionNumber}/{totalParents}
            </span>
            <span className="text-[11px] text-muted-foreground tabular shrink-0 hidden sm:inline">
              · {pctAnswered}% answered
            </span>
            <span className="text-[11px] text-muted-foreground tabular shrink-0 sm:hidden">
              · {pctAnswered}%
            </span>
            <div className="flex-1 min-w-[40px] h-1 rounded-full bg-[hsl(var(--surface-2))] overflow-hidden">
              <div
                className="h-full bg-[hsl(var(--accent-red))] transition-all duration-300"
                style={{ width: `${pctAnswered}%` }}
              />
            </div>
          </div>
          {nextParentIndex !== -1 && (
            <button
              type="button"
              onClick={jumpToNextUnanswered}
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 min-h-[40px] rounded border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--surface-2))] hover:border-[hsl(var(--accent-red))]/40 transition-colors whitespace-nowrap shrink-0"
              aria-label="Jump to next unanswered question"
            >
              <ArrowDown className="w-3.5 h-3.5" /> Next unanswered
            </button>
          )}
          {nextParentIndex === -1 && (
            <span className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-emerald-400/80 shrink-0">
              <Check className="w-3.5 h-3.5" /> All answered
            </span>
          )}
        </div>
      </div>

      {/* Header card */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-1.5">
            Dimension {dimensionIndex(dimension.dimension_id)} · {(dimension.composite_weight * 100).toFixed(0)}% of composite
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">{dimension.dimension_name}</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Answer each question. Score updates in real time. Pick "TBD" only when the AE has explicitly
            acknowledged not knowing — TBD &gt; 40% caps the dimension at Orange, &gt; 50% turns it grey.
          </p>
        </div>
        <div className="card-shell-2 px-5 py-4 min-w-[260px]">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Dimension score</div>
          <div className="flex items-baseline gap-3 mt-1">
            <span className={`text-4xl font-semibold tabular tracking-tight ${s.text}`}>
              {result.color === "tbd" ? "—" : result.score.toFixed(1)}
            </span>
            <BandPill band={result.color} capped={result.capped} />
          </div>
          <div className="mt-3" title="Composite score band">
            <BandBar band={result.color} value={result.color === "tbd" ? 0 : result.score} />
          </div>
          <div className="mt-2 text-[11px] text-muted-foreground tabular">
            {result.answeredCount}/{result.totalParentCount} parents · {pctTbd}% TBD
          </div>
        </div>
      </div>

      {/* Question list */}
      <div className="space-y-4">
        {parents.map((p, i) => (
          <ParentQuestionCard
            key={p.question_id}
            index={i + 1}
            parent={p}
            subs={subQuestionsOf(dimension, p.question_id)}
            answer={answers[p.question_id]}
            answers={answers}
            onAnswer={setAnswer}
            anchorId={`q-${p.question_id}`}
          />
        ))}
      </div>
    </OperatorShell>
  );
}

function dimensionIndex(id: string): number {
  return QBANK.dimensions.findIndex((d) => d.dimension_id === id) + 1;
}

function ParentQuestionCard({
  index,
  parent,
  subs,
  answer,
  answers,
  onAnswer,
  anchorId,
}: {
  index: number;
  parent: Question;
  subs: Question[];
  answer: string | undefined;
  answers: Record<string, string>;
  onAnswer: (qid: string, letter: string) => void;
  anchorId?: string;
}) {
  const ansOpt = answer ? findOption(parent, answer) : undefined;
  const triggers = subs.length > 0 ? subs[0].trigger_options : [];
  const subsActive = answer ? triggers.includes(answer) : false;

  return (
    <div id={anchorId} className="card-shell p-5 scroll-mt-32">
      <div className="flex items-baseline justify-between gap-3 mb-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground tabular">
          Q{index} · {parent.question_id} · weight {((parent.weight_in_dimension ?? 0) * 100).toFixed(0)}%
        </div>
        {ansOpt && !ansOpt.is_tbd && (
          <span className="text-[10px] uppercase tracking-wider text-emerald-400/80 inline-flex items-center gap-1">
            <Check className="w-3 h-3" /> answered
          </span>
        )}
        {ansOpt && ansOpt.is_tbd && (
          <span className="text-[10px] uppercase tracking-wider text-neutral-500">TBD flagged</span>
        )}
      </div>
      <div className="text-[15px] text-foreground font-medium leading-snug mb-3">{parent.question_text}</div>

      <div className="space-y-2">
        {parent.answer_options.map((o) => (
          <Bubble
            key={o.option_id}
            option={o}
            selected={answer === o.letter}
            onSelect={() => onAnswer(parent.question_id, o.letter)}
          />
        ))}
      </div>

      {subsActive && subs.length > 0 && (
        <div className="mt-5 pl-5 border-l-2 border-border space-y-4">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Follow-ups (triggered by answer {answer})
          </div>
          {subs.map((sq) => {
            const subAns = answers[sq.question_id];
            const subAnsOpt = subAns ? findOption(sq, subAns) : undefined;
            return (
              <div key={sq.question_id}>
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground tabular">
                    {sq.question_id} · sub-weight {((sq.weight_in_sub_block ?? 0) * 100).toFixed(0)}%
                  </div>
                  {subAnsOpt && !subAnsOpt.is_tbd && (
                    <span className="text-[10px] uppercase tracking-wider text-emerald-400/80 inline-flex items-center gap-1">
                      <Check className="w-3 h-3" /> answered
                    </span>
                  )}
                  {subAnsOpt && subAnsOpt.is_tbd && (
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">TBD flagged</span>
                  )}
                </div>
                <div className="text-[14px] text-foreground leading-snug mb-2">{sq.question_text}</div>
                <div className="space-y-1.5">
                  {sq.answer_options.map((o) => (
                    <Bubble
                      key={o.option_id}
                      option={o}
                      selected={subAns === o.letter}
                      onSelect={() => onAnswer(sq.question_id, o.letter)}
                      compact
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Bubble({
  option,
  selected,
  onSelect,
  compact = false,
}: {
  option: AnswerOption;
  selected: boolean;
  onSelect: () => void;
  compact?: boolean;
}) {
  const isTbd = option.is_tbd;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-md border transition-colors flex items-start gap-3 ${
        compact ? "px-3 py-2" : "px-4 py-3"
      } ${
        selected
          ? isTbd
            ? "bg-neutral-700/40 border-neutral-500 text-neutral-100"
            : "bg-[hsl(var(--surface-2))] border-[hsl(var(--accent-red))] text-foreground"
          : "bg-transparent border-border text-muted-foreground hover:bg-[hsl(var(--surface-2))]/60 hover:text-foreground"
      }`}
    >
      <span
        className={`mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-medium tabular shrink-0 ${
          selected
            ? isTbd
              ? "bg-neutral-400 text-neutral-900"
              : "bg-[hsl(var(--accent-red))] text-white"
            : "bg-[hsl(var(--muted))] text-muted-foreground"
        }`}
      >
        {option.letter}
      </span>
      <span className="flex-1 min-w-0">
        <span className={`block text-[13px] ${compact ? "leading-snug" : "leading-relaxed"}`}>
          {option.label}
        </span>
        {!compact && option.meaning && !isTbd && (
          <span className="block text-[11px] text-muted-foreground mt-1 leading-relaxed">
            {option.meaning}
          </span>
        )}
      </span>
      <span className="text-[10px] tabular text-muted-foreground shrink-0 mt-1">
        {isTbd ? "TBD" : `${option.score} pts`}
      </span>
    </button>
  );
}
